import React from 'react';
import { doc } from 'firebase/firestore';
import { updateDoc } from '../../api/FirestoreInterceptor';
import { db } from '../../config-firebase/firebase.js';
import { fetchInDataChanges } from '../../api/Api.js';
import { requestSorter } from '../../Helpers/Helpers.js';
import {
  playNewOrderAlert,
  playWaiterCallAlert,
  playPaymentCallAlert,
  playStatusChangeAlert,
} from '../../Helpers/soundAlerts.js';

export const usePdvRealtimeOrders = ({
  setRequestDoneList,
  fetchData,
  setShowPaymentBlockedPopup,
  setSelectedItemToPay,
}) => {
  const [pendingWaiterCalls, setPendingWaiterCalls] = React.useState([]);
  const [pendingPaymentCalls, setPendingPaymentCalls] = React.useState([]);
  const [inspectedCalls, setInspectedCalls] = React.useState(new Set());
  const [inspectedPaymentCalls, setInspectedPaymentCalls] = React.useState(new Set());
  const inspectedCallsRef = React.useRef(new Set());
  const inspectedPaymentCallsRef = React.useRef(new Set());

  // Checkbox de warning: por padrão ativo (true). Se desmarcado, apenas suprime os popups na tela.
  const [warningsEnabled, setWarningsEnabled] = React.useState(() => {
    const saved = localStorage.getItem('pdvWarningsEnabled');
    return saved !== null ? saved === 'true' : true;
  });

  const warningsEnabledRef = React.useRef(warningsEnabled);
  React.useEffect(() => {
    warningsEnabledRef.current = warningsEnabled;
  }, [warningsEnabled]);

  const handleToggleWarnings = (checked) => {
    setWarningsEnabled(checked);
    localStorage.setItem('pdvWarningsEnabled', String(checked));
    if (!checked) {
      if (setShowPaymentBlockedPopup) setShowPaymentBlockedPopup(false);
      if (setSelectedItemToPay) setSelectedItemToPay(null);
    }
  };

  const dismissWaiterCall = async (id) => {
    try {
      inspectedCallsRef.current.delete(id);
      setInspectedCalls((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      await updateDoc(doc(db, 'requests', id), {
        'waiterCall.active': false,
        'waiterCall.popupDismissed': false,
        'waiterCall.checked': true,
        'waiterCall.checkedAt': Date.now(),
      });
    } catch (e) {
      console.error('Erro ao concluir chamado do garçom:', e);
    }
  };

  const dismissPaymentCall = async (id) => {
    try {
      inspectedPaymentCallsRef.current.delete(id);
      setInspectedPaymentCalls((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setPendingPaymentCalls((prev) => prev.filter((p) => p.id !== id));
      await updateDoc(doc(db, 'requests', id), {
        'paymentCall.active': false,
      });
    } catch (e) {
      console.error('Erro ao concluir chamado de conta:', e);
    }
  };

  // Rastreamento para alertas sonoros no PDV
  const isInitialSnapshotRef = React.useRef(true);
  const prevOrdersSnapshotRef = React.useRef(new Map());
  const prevWaiterCallIdsRef = React.useRef(new Set());
  const prevPaymentCallIdsRef = React.useRef(new Set());

  const getOrderStateSignature = (order) => {
    const itemsSignature = (order.request || [])
      .map(
        (dish, i) =>
          `${i}_${dish.name || dish.id}_pronto:${Boolean(dish.pronto)}_entregue:${Boolean(dish.entregue)}_deliv:${Boolean(dish.deliveredByWaiter)}`
      )
      .join('|');

    return {
      paymentDone: Boolean(order.paymentDone),
      done: Boolean(order.done),
      orderDelivered: Boolean(order.orderDelivered),
      itemsSignature,
    };
  };

  React.useEffect(() => {
    const unsubscribe = fetchInDataChanges('requests', (data) => {
      let requestList = data.filter((item) => !item.orderDelivered);
      requestList = requestSorter(requestList);

      setRequestDoneList(requestList);

      const newWaiterCalls = requestList.filter(
        (req) => req.waiterCall && req.waiterCall.active === true && !req.waiterCall.popupDismissed
      );

      setPendingWaiterCalls((prevCalls) => {
        const keepCalls = prevCalls.filter((pc) => newWaiterCalls.some((nc) => nc.id === pc.id));
        const addCalls = newWaiterCalls.filter((nc) => !prevCalls.some((pc) => pc.id === nc.id));
        return [...keepCalls, ...addCalls];
      });

      const newPaymentCalls = requestList.filter(
        (req) => req.paymentCall && req.paymentCall.active === true
      );

      setPendingPaymentCalls((prevCalls) => {
        const keepCalls = prevCalls.filter((pc) => newPaymentCalls.some((nc) => nc.id === pc.id));
        const addCalls = newPaymentCalls.filter((nc) => !prevCalls.some((pc) => pc.id === nc.id));
        return [...keepCalls, ...addCalls];
      });

      if (isInitialSnapshotRef.current) {
        const initialMap = new Map();
        const initialWaiter = new Set();
        const initialPayment = new Set();

        requestList.forEach((order) => {
          initialMap.set(order.id, getOrderStateSignature(order));
          if (order.waiterCall && order.waiterCall.active === true) {
            initialWaiter.add(order.id);
          }
          if (order.paymentCall && order.paymentCall.active === true) {
            initialPayment.add(order.id);
          }
        });

        prevOrdersSnapshotRef.current = initialMap;
        prevWaiterCallIdsRef.current = initialWaiter;
        prevPaymentCallIdsRef.current = initialPayment;
        isInitialSnapshotRef.current = false;
      } else {
        let hasNewWaiterCall = false;
        let hasNewPaymentCall = false;
        let hasNewOrder = false;
        let hasStatusChange = false;

        const currentWaiterIds = new Set();
        const currentPaymentIds = new Set();
        const nextOrdersMap = new Map();

        requestList.forEach((order) => {
          const currentSig = getOrderStateSignature(order);
          nextOrdersMap.set(order.id, currentSig);

          if (order.waiterCall && order.waiterCall.active === true) {
            currentWaiterIds.add(order.id);
            if (!prevWaiterCallIdsRef.current.has(order.id)) {
              hasNewWaiterCall = true;
            }
          }

          if (order.paymentCall && order.paymentCall.active === true) {
            currentPaymentIds.add(order.id);
            if (!prevPaymentCallIdsRef.current.has(order.id)) {
              hasNewPaymentCall = true;
            }
          }

          if (!prevOrdersSnapshotRef.current.has(order.id)) {
            hasNewOrder = true;
          } else {
            const prevSig = prevOrdersSnapshotRef.current.get(order.id);
            if (
              prevSig.paymentDone !== currentSig.paymentDone ||
              prevSig.done !== currentSig.done ||
              prevSig.itemsSignature !== currentSig.itemsSignature
            ) {
              hasStatusChange = true;
            }
          }
        });

        if (warningsEnabledRef.current && hasNewWaiterCall) {
          playWaiterCallAlert();
        } else if (warningsEnabledRef.current && hasNewPaymentCall) {
          playPaymentCallAlert();
        } else if (hasNewOrder) {
          playNewOrderAlert();
        } else if (hasStatusChange) {
          playStatusChangeAlert();
        }

        prevOrdersSnapshotRef.current = nextOrdersMap;
        prevWaiterCallIdsRef.current = currentWaiterIds;
        prevPaymentCallIdsRef.current = currentPaymentIds;
      }
    });

    if (fetchData) fetchData();

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  return {
    pendingWaiterCalls,
    setPendingWaiterCalls,
    pendingPaymentCalls,
    setPendingPaymentCalls,
    inspectedCalls,
    setInspectedCalls,
    inspectedPaymentCalls,
    setInspectedPaymentCalls,
    inspectedCallsRef,
    inspectedPaymentCallsRef,
    warningsEnabled,
    handleToggleWarnings,
    dismissWaiterCall,
    dismissPaymentCall,
  };
};

